import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button, Checkbox, Dialog, EmptyState, SearchField, Skeleton } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useCommissionModels, useMemberModels, useSetModelMembers } from "@/hooks/useCommissions";
import { commissionErrorKey } from "@/lib/commissions/api";
import type { CommissionModel } from "@/lib/commissions/types";
import { getInitials } from "@/lib/utils/string-utils";
import { cn } from "@/lib/utils";
import { formatList } from "@/lib/format/list";

type Props = { model: CommissionModel | null; open: boolean; onOpenChange: (open: boolean) => void };

const norm = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
const firstName = (full: string) => full.split(" ")[0];

/** Picks exactly who uses a model. Each person has one model, so checking someone moves them. */
export function AssignModelDialog({ model, open, onOpenChange }: Props) {
  const { t, i18n } = useTranslation("commissions");
  const { data: members = [], isLoading } = useMemberModels(open);
  const { data: models = [] } = useCommissionModels(open);
  const save = useSetModelMembers();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !model) return;
    setSelected(new Set(members.filter((m) => m.model_id === model.id).map((m) => m.user_id)));
    setQuery("");
  }, [open, model, members]);

  const modelName = useMemo(() => new Map(models.map((m) => [m.id, m.name])), [models]);
  const visible = members.filter((m) => !query.trim() || norm(m.full_name).includes(norm(query.trim())));
  const changes = members.filter((m) => (m.model_id === model?.id) !== selected.has(m.user_id));
  const moves = changes.filter((m) => selected.has(m.user_id) && m.model_id);

  if (!model) return null;

  const onSave = async () => {
    try {
      await save.mutateAsync({ modelId: model.id, userIds: [...selected] });
      toast({ title: t("assign.saved") });
      onOpenChange(false);
    } catch (error) {
      toast({ title: t(commissionErrorKey(error)), variant: "destructive" });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("assign.title", { model: model.name })}
      footer={
        <div className="flex w-full flex-col gap-3">
          {moves.length > 0 && (
            <p role="status" className="rounded-md bg-state-warning-soft px-3 py-2 text-sm text-state-warning-foreground">
              {t("assign.moves", {
                list: formatList(moves.map((m) => t("assign.moveOne", { name: firstName(m.full_name), model: modelName.get(m.model_id!) ?? "" })), i18n.language),
              })}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("assign.cancel")}</Button>
            <Button type="button" onClick={onSave} loading={save.isPending} disabled={changes.length === 0}>
              {changes.length === 0 ? t("assign.save") : t("assign.saveCount", { count: changes.length })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t("assign.description")}</p>
        <SearchField
          label={t("assign.search")}
          placeholder={t("assign.search")}
          clearLabel={t("assign.clearSearch")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery("")}
        />
        {isLoading ? (
          <Skeleton label={t("assign.loading")} className="h-48 w-full" />
        ) : visible.length === 0 ? (
          <EmptyState title={t("assign.empty")} />
        ) : (
          <ul className="flex max-h-[50vh] flex-col gap-0.5 overflow-y-auto" aria-label={t("assign.listLabel")}>
            {visible.map((m) => {
              const checked = selected.has(m.user_id);
              const current = checked ? model.id : m.model_id === model.id ? null : m.model_id;
              return (
                <li key={m.user_id}>
                  <div
                    className={cn(
                      "flex min-h-14 items-center gap-3 rounded-md px-3 py-1.5",
                      "hover:bg-muted",
                      checked && "bg-accent",
                    )}
                  >
                    <Avatar name={m.full_name} fallback={getInitials(m.full_name)} aria-hidden="true" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      {/* The checkbox's own label is the person's name, so the whole name is a click target. */}
                      <Checkbox
                        className="min-w-0 font-semibold"
                        label={m.full_name}
                        checked={checked}
                        aria-describedby={`assign-${m.user_id}-detail`}
                        onCheckedChange={(next) =>
                          setSelected((s) => {
                            const copy = new Set(s);
                            if (next) copy.add(m.user_id);
                            else copy.delete(m.user_id);
                            return copy;
                          })
                        }
                      />
                      <span id={`assign-${m.user_id}-detail`} className="truncate pl-7 text-xs text-muted-foreground">
                        {m.job_title ?? ""}
                      </span>
                    </div>
                    {current ? (
                      <Badge variant={checked ? "warning" : "default"} className="shrink-0">{modelName.get(current) ?? ""}</Badge>
                    ) : (
                      <Badge className="shrink-0">{t("assign.noModel")}</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
